import { useCallback, useEffect, useRef, useState } from "react";

// Minimal slice of the YT IFrame Player API surface we use.
interface YTPlayer {
  getCurrentTime: () => number;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  playVideo: () => void;
  mute: () => void;
  unMute: () => void;
  destroy: () => void;
}

interface YTStateChangeEvent {
  data: number;
  target: YTPlayer;
}

interface YTPlayerCtor {
  new (
    el: HTMLElement,
    options: {
      videoId: string;
      playerVars?: Record<string, string | number>;
      events?: {
        onReady?: () => void;
        onStateChange?: (event: YTStateChangeEvent) => void;
      };
    },
  ): YTPlayer;
}

declare global {
  interface Window {
    YT?: {
      Player: YTPlayerCtor;
      PlayerState?: {
        UNSTARTED: number;
        ENDED: number;
        PLAYING: number;
        PAUSED: number;
        BUFFERING: number;
        CUED: number;
      };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

// Module-level promise — load the script once per page, even across hook calls.
let iframeApiPromise: Promise<void> | null = null;

function loadIframeApi(): Promise<void> {
  if (iframeApiPromise) return iframeApiPromise;
  if (window.YT?.Player) return Promise.resolve();
  iframeApiPromise = new Promise<void>((resolve) => {
    const prior = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prior?.();
      resolve();
    };
    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    script.async = true;
    document.head.appendChild(script);
  });
  return iframeApiPromise;
}

export interface YouTubePlayerHandle {
  setContainer: (el: HTMLDivElement | null) => void;
  getCurrentTime: () => number;
  seekTo: (seconds: number) => void;
}

// Window during which a PLAYING state-change after our own seekTo() is
// attributed to the programmatic autoplay we just kicked off, not a real
// user gesture. 2s comfortably covers buffering on a fresh load while still
// being short enough that a separate user play right after won't get
// swallowed.
export const PROGRAMMATIC_PLAY_WINDOW_MS = 2000;

export interface AutoUnmuteState {
  userInteracted: boolean;
  lastSeekAt: number;
}

export function createAutoUnmuteState(): AutoUnmuteState {
  return { userInteracted: false, lastSeekAt: 0 };
}

// Returns true if a PLAYING state-change at `now` should be treated as a user
// gesture and trigger unmute. Returns false if we've already unmuted, or if
// this PLAYING is plausibly our own post-seekTo autoplay.
export function shouldUnmuteOnPlaying(state: AutoUnmuteState, now: number): boolean {
  if (state.userInteracted) return false;
  if (state.lastSeekAt > 0 && now - state.lastSeekAt < PROGRAMMATIC_PLAY_WINDOW_MS) {
    return false;
  }
  return true;
}

export function shouldUnmuteOnContainerClick(state: AutoUnmuteState): boolean {
  return !state.userInteracted;
}

// State-backed callback ref so the effect fires the moment the container element
// attaches — even if the consumer hides the container behind a `loading` branch on
// the first render.
export function useYouTubePlayer(videoId: string): YouTubePlayerHandle {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const autoUnmuteRef = useRef<AutoUnmuteState>(createAutoUnmuteState());

  useEffect(() => {
    if (!container) return;
    let cancelled = false;
    let created: YTPlayer | null = null;
    // A fresh mount is a fresh slate — the previous "already unmuted" flag
    // doesn't carry over to a brand-new player instance.
    autoUnmuteRef.current = createAutoUnmuteState();

    function tryUnmute() {
      const p = playerRef.current;
      if (!p) return;
      autoUnmuteRef.current.userInteracted = true;
      try {
        p.unMute();
      } catch {
        // unMute can throw if the iframe is mid-teardown; ignore.
      }
    }

    function onContainerClick() {
      if (shouldUnmuteOnContainerClick(autoUnmuteRef.current)) tryUnmute();
    }

    void (async () => {
      await loadIframeApi();
      if (cancelled || !window.YT?.Player) return;
      // YT.Player replaces the element it's handed with an <iframe>, so we
      // append a throwaway child div under our container element and let the
      // API chew that up — keeps our outer container stable across re-renders.
      const mount = document.createElement("div");
      mount.style.width = "100%";
      mount.style.height = "100%";
      container.replaceChildren(mount);
      created = new window.YT.Player(mount, {
        videoId,
        // Mount muted so a programmatic seekTo() can autoplay under Chrome's
        // autoplay policy (muted autoplay is permitted; unmuted requires a
        // user gesture). The first real user gesture flips back to unmuted —
        // see onStateChange below + the container click listener.
        playerVars: { rel: 0, mute: 1, playsinline: 1, autoplay: 0 },
        events: {
          onStateChange: (event) => {
            const PLAYING = window.YT?.PlayerState?.PLAYING ?? 1;
            if (event.data !== PLAYING) return;
            if (shouldUnmuteOnPlaying(autoUnmuteRef.current, Date.now())) {
              tryUnmute();
            }
          },
        },
      });
      playerRef.current = created;
    })();

    container.addEventListener("click", onContainerClick);

    return () => {
      cancelled = true;
      container.removeEventListener("click", onContainerClick);
      if (created && typeof created.destroy === "function") {
        try {
          created.destroy();
        } catch {
          // YT.Player.destroy can throw if the iframe is already gone; ignore.
        }
      }
      playerRef.current = null;
    };
  }, [container, videoId]);

  const getCurrentTime = useCallback((): number => {
    const p = playerRef.current;
    if (!p) return 0;
    const t = Number(p.getCurrentTime());
    return Number.isFinite(t) && t >= 0 ? t : 0;
  }, []);

  const seekTo = useCallback((seconds: number) => {
    const p = playerRef.current;
    if (!p) return;
    autoUnmuteRef.current.lastSeekAt = Date.now();
    p.seekTo(Math.max(0, Math.floor(seconds)), true);
    p.playVideo();
  }, []);

  return { setContainer, getCurrentTime, seekTo };
}
